import { useState, useEffect } from 'react';
import { aiManagementApi } from '../../api/aiManagement.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner, EmptyState, Badge } from '../../components/ui/index';
import { toPersian, formatDate } from '../../utils/persianNumber';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { FolderOpen } from '@phosphor-icons/react';

const RATING_CONFIG = {
  correct: { label: 'صحیح', variant: 'green' },
  needs_improvement: { label: 'نیاز به بهبود', variant: 'amber' },
  wrong: { label: 'اشتباه', variant: 'red' },
};

function toCSV(rows) {
  const cols = ['sample_id', 'user_question', 'original_ai_answer', 'final_answer', 'department', 'category', 'satisfaction', 'quality_rating', 'editor', 'created_date', 'reason'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
}

export default function TrainingDatasetPage() {
  const { addToast } = useUI();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState('');

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    return aiManagementApi.exportTrainingDataset({ quality_rating: rating || undefined })
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => { if (showSpinner) addToast('خطا در بارگذاری دیتاست', 'error'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [rating]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(() => load(false));

  const downloadCSV = () => {
    const blob = new Blob([toCSV(items)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ai_training_dataset_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadJSONL = () => {
    const text = items.map(r => JSON.stringify(r)).join('\n');
    const blob = new Blob([text], { type: 'application/jsonl;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ai_training_dataset_${Date.now()}.jsonl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fade-in">
      <PageHeader title="دیتاست آموزشی AI" subtitle={`${toPersian(items.length)} نمونه بررسی‌شده توسط مدیران`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="secondary" onClick={downloadCSV} disabled={items.length === 0}>خروجی CSV</Btn>
            <Btn variant="secondary" onClick={downloadJSONL} disabled={items.length === 0}>خروجی JSONL</Btn>
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['', 'همه'], ['correct', 'صحیح'], ['needs_improvement', 'نیاز به بهبود'], ['wrong', 'اشتباه']].map(([id, label]) => (
          <button key={id} onClick={() => setRating(id)} style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${rating === id ? '#7c3aed' : '#e2e8f0'}`, background: rating === id ? '#f3e8ff' : '#fff', color: rating === id ? '#6d28d9' : '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
        ))}
      </div>

      {loading ? <Spinner /> : items.length === 0 ? <EmptyState icon={<FolderOpen size={44} weight="regular" />} title="نمونه‌ای ثبت نشده" subtitle="از صفحه بررسی پاسخ‌های AI، پاسخ‌ها را بررسی و بهبود دهید" /> : (
        <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'right' }}>
                {['سوال', 'پاسخ نهایی', 'دپارتمان', 'وضعیت', 'ویرایشگر', 'تاریخ'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(r => {
                const conf = RATING_CONFIG[r.quality_rating] || RATING_CONFIG.needs_improvement;
                return (
                  <tr key={r.sample_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', color: '#334155', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.user_question}</td>
                    <td style={{ padding: '10px 14px', color: '#334155', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.final_answer}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{r.department || '—'}</td>
                    <td style={{ padding: '10px 14px' }}><Badge variant={conf.variant}>{conf.label}</Badge></td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{r.editor || '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#94a3b8' }}>{formatDate(r.created_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
